/**
 * The demo's Marketing › Advertising: the overview, its trend and the Meta-vs-TmT
 * reconciliation, from one small set of fictional campaigns with round daily rates —
 * roughly the size of a competitions operator spending £35 a day on Meta. Nothing here
 * is a real tenant's number.
 *
 * It reads the demo Meta connection (`features/integrations/mock.ts`) as the campaign
 * report's demo does, so a disconnected or unmapped demo has nothing imported: every
 * KPI not available, no campaigns, no reconciliation rows — never a 0 standing in for
 * "unknown". The figures follow the READMEs' own rules, so the demo can be trusted to
 * exercise the page: KPI ratios from summed counters, the KPI's attributed revenue equal
 * to the campaign rows plus the unmatched paid part, the trend summing to the KPI row,
 * and the reconciliation's totals equal to its rows plus its lines.
 */

import type {
  AdAttributionReconciliation,
  AdCampaignRow,
  AdKpi,
  AdKpiKey,
  AdKpiNullReason,
  AdOverviewQuery,
  AdOverviewTrendQuery,
  AdReconciliationQuery,
  AdTrendPoint,
  AdvertisingOverview,
  AdvertisingOverviewTrend,
  MetaReported,
  OverviewReconciliation,
  ProviderFigures,
  ReconciliationCause,
  ReconciliationDiff,
  ReconciliationFigures,
  ReconciliationMetaOnly,
  ReconciliationObjectRow,
  ReconciliationTotal,
  TmtAttributed,
  TmtFigures,
} from '@/api/advertisingOverview'
import type { AdvertisingFreshness, AdvertisingLevel, AttributionModel, MetaIntegrationState } from '@/api/types'
import { addDays, dayCount, resolvePeriod, type GlobalFilters } from '@/lib/filters'
import { demoDataQuality, demoSiteCoverage } from '@/mock/dataQuality'

/** The API's `attribution.definitions.LOOKBACK`. */
const LOOKBACK_DAYS = 30
const ATTRIBUTION_WINDOWS = ['7d_click,1d_view']
/** The previous period runs at this share of the current one's rates. */
const PREVIOUS_FACTOR = 0.85
/** Another model than the default (paid click) credits fewer orders to an ad: the demo's stand-in for the credit moving. */
const MODEL_FACTOR: Record<AttributionModel, number> = { paid_click: 1, last_non_direct: 0.95, last_touch: 0.9, first_touch: 0.8 }
/** Every completed order, attributed or not (`all_sales`): about five times the demo's spend, at 676's average order. */
const ALL_SALES = { ordersPerDay: 8, averageOrderMinor: 2144 }

interface DailyRates {
  spendMinor: number
  impressions: number
  clicks: number
  tmtPurchases: number
  tmtRevenuePerPurchaseMinor: number
  newCustomerShare: number
  metaPurchases: number
  metaRevenuePerPurchaseMinor: number
}

interface DemoChild {
  id: string
  name: string
  /** Its share of the parent's figures. */
  share: number
  children?: readonly DemoChild[]
}

interface DemoCampaign {
  id: string
  name: string
  status: string
  effectiveStatus: string
  rates: DailyRates
  adSets: readonly DemoChild[]
}

const CAMPAIGNS: readonly DemoCampaign[] = [
  {
    id: 'adcmp_demo_dreamcar',
    name: 'Dream Car Draw · Prospecting',
    status: 'ACTIVE',
    effectiveStatus: 'ACTIVE',
    rates: {
      spendMinor: 1800,
      impressions: 4000,
      clicks: 64,
      tmtPurchases: 0.6,
      tmtRevenuePerPurchaseMinor: 4500,
      newCustomerShare: 0.5,
      metaPurchases: 0.8,
      metaRevenuePerPurchaseMinor: 4200,
    },
    adSets: [
      {
        id: 'adset_demo_dreamcar_uk',
        name: 'UK 25-54',
        share: 0.6,
        children: [
          { id: 'ad_demo_dreamcar_video', name: 'Supercar reveal (video)', share: 0.5 },
          { id: 'ad_demo_dreamcar_carousel', name: 'Prize carousel', share: 0.5 },
        ],
      },
      { id: 'adset_demo_dreamcar_lal', name: 'Lookalike 1%', share: 0.4, children: [{ id: 'ad_demo_dreamcar_static', name: 'Keys in hand', share: 1 }] },
    ],
  },
  {
    id: 'adcmp_demo_cashfriday',
    name: 'Cash Friday · Retargeting',
    status: 'ACTIVE',
    effectiveStatus: 'ACTIVE',
    rates: {
      spendMinor: 1000,
      impressions: 1600,
      clicks: 40,
      tmtPurchases: 0.5,
      tmtRevenuePerPurchaseMinor: 3000,
      newCustomerShare: 0.2,
      metaPurchases: 0.6,
      metaRevenuePerPurchaseMinor: 3000,
    },
    adSets: [
      { id: 'adset_demo_cashfriday_basket', name: 'Basket abandoners 7d', share: 1, children: [{ id: 'ad_demo_cashfriday_static', name: '£1,000 cash, Friday', share: 1 }] },
    ],
  },
  {
    id: 'adcmp_demo_instawins',
    name: 'Instant Wins · Video',
    status: 'ACTIVE',
    effectiveStatus: 'ACTIVE',
    rates: {
      spendMinor: 700,
      impressions: 3600,
      clicks: 28,
      tmtPurchases: 0.1,
      tmtRevenuePerPurchaseMinor: 2500,
      newCustomerShare: 0.6,
      metaPurchases: 0.2,
      metaRevenuePerPurchaseMinor: 2500,
    },
    adSets: [
      { id: 'adset_demo_instawins_broad', name: 'Broad UK 18+', share: 1, children: [{ id: 'ad_demo_instawins_reel', name: 'Scratch-card reel', share: 1 }] },
    ],
  },
  {
    // Paused before the range: an ad-level spend row of 0, so a campaign row with no ROAS.
    id: 'adcmp_demo_techbundle',
    name: 'Tech Bundle · Paused',
    status: 'PAUSED',
    effectiveStatus: 'PAUSED',
    rates: {
      spendMinor: 0,
      impressions: 0,
      clicks: 0,
      tmtPurchases: 0,
      tmtRevenuePerPurchaseMinor: 0,
      newCustomerShare: 0,
      metaPurchases: 0,
      metaRevenuePerPurchaseMinor: 0,
    },
    adSets: [
      { id: 'adset_demo_techbundle', name: 'Gadget fans', share: 1, children: [{ id: 'ad_demo_techbundle', name: 'Console + TV', share: 1 }] },
    ],
  },
]

/** Paid clicks that resolved to no imported ad: in the KPI, never in a campaign row. */
const UNMATCHED_PAID = { purchasesPerDay: 0.1, revenuePerPurchaseMinor: 2000, newCustomerShare: 0.5 }

/**
 * A campaign from an unmapped ad account, listed only because TmT credited purchases to
 * it: Meta's side is `not_imported` (null, never 0).
 */
const NOT_IMPORTED = { id: 'adcmp_demo_summer25', name: 'Summer Giveaway 2025', purchasesPerDay: 0.05, revenuePerPurchaseMinor: 2000 }

/** Returned verbatim by the API (`ad_attribution_reconciliation/README.md`, *Why the two differ*). */
export const DEMO_CAUSES: readonly ReconciliationCause[] = [
  {
    code: 'view_through',
    text: 'Meta counts a purchase up to 1 day after someone only saw an ad (1-day view-through). TmT credits visits, so a purchase after a view with no click is never TmT-attributed to an ad.',
  },
  {
    code: 'click_window',
    text: `Meta counts a purchase up to 7 days after a click (7-day click). TmT credits a visit up to ${LOOKBACK_DAYS} days before the order. A purchase 8 to ${LOOKBACK_DAYS} days after the click is TmT's only.`,
  },
  {
    code: 'tmt_model',
    text: "TmT credits each order to one visit, chosen by the selected attribution model; another model can credit the same order to a different ad or to no ad. Meta's figures don't change with the model. The default, Paid click, credits the latest paid click in the 7 days before the order (Meta's own click window; a Meta ad click counts with or without its fbclid) and, without one, the last visit that wasn't direct — so an order Meta counts for a click TmT saw is credited to that click.",
  },
  {
    code: 'omni_purchase',
    text: "Meta's purchases are its omni_purchase count: website, app and offline purchases as one. TmT counts the completed orders on this workspace's websites only.",
  },
  {
    code: 'consent',
    text: 'Visitors who declined analytics consent have no visits recorded, so their orders are never TmT-attributed to an ad. Meta may still count them.',
  },
  {
    code: 'unlinked_visits',
    text: "A visit is linked to a Meta ad only when it is a Meta click — a paid link (a paid utm_medium such as paid_social) that carries Meta's click id (fbclid) or comes from Meta (utm_source fb, ig, an, msg, facebook, instagram or messenger) — and its UTM parameters name an imported Meta campaign, ad set or ad: by id (in utm_content, utm_term, utm_campaign or utm_id), or by name (utm_campaign the campaign's name, utm_term an ad set's within it, utm_content an ad's within that ad set). It is linked as far down as they name one object, so a renamed or ambiguous ad counts at its ad set or campaign. A visit missing either is not linked to an ad: with a paid medium it is counted under 'Paid, no Meta object'; without one it is not in this panel at all. A fbclid alone doesn't make a visit paid: Facebook and Instagram add one to organic post links too.",
  },
  {
    code: 'booking_day',
    text: 'Meta books a purchase on the day of the impression or click; TmT books it on the day the order completed. Near the start and end of a range, a purchase can fall inside the range on one side and outside it on the other.',
  },
  {
    code: 'timezone',
    text: "Meta's days are the ad account's timezone; TmT's are this workspace's reporting timezone. Where the two differ, each day starts at a different time.",
  },
  {
    code: 'purchase_value',
    text: "Meta's revenue is the purchase value the Meta pixel or Conversions API sent; TmT's is the order's gross. Neither side subtracts refunds.",
  },
]

// --- arithmetic, by the API's rules ------------------------------------------------

function round4(value: number): number {
  return Math.round(value * 10_000) / 10_000
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}

/** `(current − previous) ÷ |previous| × 100`, two places; null over a null or a 0. */
function deltaPct(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null
  return round2(((current - previous) / Math.abs(previous)) * 100)
}

/** Totals for one object over `days`, at `factor` of its rates. */
interface Counters {
  spendMinor: number
  impressions: number
  clicks: number
  tmtPurchases: number
  tmtRevenueMinor: number
  newCustomers: number
  metaPurchases: number
  metaRevenueMinor: number
}

function countersFor(rates: DailyRates, days: number, factor: number, model: AttributionModel): Counters {
  const tmtPurchases = Math.round(rates.tmtPurchases * days * factor * MODEL_FACTOR[model])
  const metaPurchases = Math.round(rates.metaPurchases * days * factor)
  return {
    spendMinor: Math.round(rates.spendMinor * days * factor),
    impressions: Math.round(rates.impressions * days * factor),
    clicks: Math.round(rates.clicks * days * factor),
    tmtPurchases,
    tmtRevenueMinor: tmtPurchases * rates.tmtRevenuePerPurchaseMinor,
    newCustomers: Math.round(tmtPurchases * rates.newCustomerShare),
    metaPurchases,
    metaRevenueMinor: metaPurchases * rates.metaRevenuePerPurchaseMinor,
  }
}

function sumCounters(list: readonly Counters[]): Counters {
  const total: Counters = {
    spendMinor: 0,
    impressions: 0,
    clicks: 0,
    tmtPurchases: 0,
    tmtRevenueMinor: 0,
    newCustomers: 0,
    metaPurchases: 0,
    metaRevenueMinor: 0,
  }
  for (const item of list) for (const key of Object.keys(total) as (keyof Counters)[]) total[key] += item[key]
  return total
}

function ratio(numerator: number, denominator: number): number | null {
  return denominator === 0 ? null : round4(numerator / denominator)
}

function perUnit(minor: number, count: number): number | null {
  return count === 0 ? null : Math.round(minor / count)
}

function tmtFigures(c: Counters): TmtFigures {
  return {
    source: 'tmt',
    purchases: c.tmtPurchases,
    revenueMinor: c.tmtRevenueMinor,
    newCustomers: c.newCustomers,
    cpaMinor: perUnit(c.spendMinor, c.tmtPurchases),
    roas: ratio(c.tmtRevenueMinor, c.spendMinor),
    costPerNewCustomerMinor: perUnit(c.spendMinor, c.newCustomers),
  }
}

function providerFigures(c: Counters): ProviderFigures {
  return {
    source: 'meta',
    purchases: c.metaPurchases,
    revenueMinor: c.metaRevenueMinor,
    roas: ratio(c.metaRevenueMinor, c.spendMinor),
    cpaMinor: perUnit(c.spendMinor, c.metaPurchases),
  }
}

/** Splits an integer total by `shares` so the parts always add back to it. */
function splitInteger(total: number, shares: readonly number[]): number[] {
  const sum = shares.reduce((acc, share) => acc + share, 0)
  let cumulative = 0
  let assigned = 0
  return shares.map((share) => {
    cumulative += share
    const upTo = Math.round((total * cumulative) / sum)
    const part = upTo - assigned
    assigned = upTo
    return part
  })
}

/** A weekly wave by the calendar day, so the demo's busy days stay put whatever the range. */
function weight(day: string, phase: number): number {
  const epochDay = Math.round(Date.parse(`${day}T00:00:00Z`) / 86_400_000)
  return 1 + 0.35 * Math.sin((2 * Math.PI * (epochDay + phase)) / 7)
}

// --- the store --------------------------------------------------------------------

/** Each KPI's unit and source (`definitions.KPIS`). */
const KPI_UNITS: Record<AdKpiKey, Pick<AdKpi, 'unit' | 'source'>> = {
  ad_spend: { unit: 'currency_minor', source: 'meta' },
  attributed_revenue: { unit: 'currency_minor', source: 'tmt' },
  roas: { unit: 'ratio', source: 'tmt' },
  revenue_per_ad_spend: { unit: 'ratio', source: 'all_sales' },
  purchases: { unit: 'count', source: 'tmt' },
  cpa: { unit: 'currency_minor', source: 'tmt' },
  cost_per_new_customer: { unit: 'currency_minor', source: 'tmt' },
  ctr: { unit: 'ratio', source: 'meta' },
  cpc: { unit: 'currency_minor', source: 'meta' },
  cpm: { unit: 'currency_minor', source: 'meta' },
}

/** A KPI's figure and why it is null, if it is. */
type Figure = [number | null, AdKpiNullReason | null]

interface PeriodFigures {
  values: Record<AdKpiKey, number | null>
  reasons: Partial<Record<AdKpiKey, AdKpiNullReason>>
}

export class MockAdvertisingOverviewStore {
  constructor(
    private readonly connection: () => MetaIntegrationState,
    private readonly currency: () => string,
    private readonly now: () => Date,
    private readonly baseline: () => string | null = () => null,
  ) {}

  /** Something is imported: Meta is connected and an ad account feeds a website. */
  private active(): boolean {
    const meta = this.connection()
    return meta.status !== 'not_connected' && meta.adAccounts.length > 0
  }

  private freshness(): AdvertisingFreshness {
    const meta = this.connection()
    const connected = meta.status !== 'not_connected'
    return {
      generatedAt: this.now().toISOString(),
      lastRecentImport: connected && meta.adAccounts.length > 0 ? meta.lastImports.insights : null,
      providerLag: connected ? meta.providerLag : false,
      connected,
      mappedAccounts: connected ? meta.adAccounts.length : 0,
    }
  }

  private range(filters: GlobalFilters): { from: string; to: string; days: number } {
    const { current } = resolvePeriod(filters, this.now())
    return { from: current.from, to: current.to, days: dayCount(current.from, current.to) }
  }

  private campaignCounters(days: number, factor: number, model: AttributionModel): Counters[] {
    return CAMPAIGNS.map((campaign) => countersFor(campaign.rates, days, factor, model))
  }

  private unmatched(days: number, factor: number, model: AttributionModel): { purchases: number; revenueMinor: number; newCustomers: number } {
    const purchases = Math.round(UNMATCHED_PAID.purchasesPerDay * days * factor * MODEL_FACTOR[model])
    return {
      purchases,
      revenueMinor: purchases * UNMATCHED_PAID.revenuePerPurchaseMinor,
      newCustomers: Math.round(purchases * UNMATCHED_PAID.newCustomerShare),
    }
  }

  /** One period's KPI figures, by the README's definitions over summed counters. */
  private period(days: number, factor: number, model: AttributionModel): PeriodFigures {
    const campaigns = sumCounters(this.campaignCounters(days, factor, model))
    const unmatched = this.unmatched(days, factor, model)
    const spend = campaigns.spendMinor
    const purchases = campaigns.tmtPurchases + unmatched.purchases
    const revenue = campaigns.tmtRevenueMinor + unmatched.revenueMinor
    const newCustomers = campaigns.newCustomers + unmatched.newCustomers
    const { impressions, clicks } = campaigns
    // Every sale, whatever the model: no attribution model changes it.
    const sales = allSales(days, factor).revenueMinor
    // Each KPI with why it is null, side by side: the README's denominator rule, in one place.
    const known = (value: number): Figure => [value, null]
    const over = (denominator: number, reason: AdKpiNullReason, divide: () => number): Figure =>
      denominator === 0 ? [null, reason] : [divide(), null]
    // CPA and cost per new customer: no spend first, then no count to divide it by.
    const spendPer = (count: number, none: AdKpiNullReason): Figure =>
      spend === 0 ? [null, 'no_spend'] : over(count, none, () => Math.round(spend / count))
    const figures: Record<AdKpiKey, Figure> = {
      ad_spend: known(spend),
      attributed_revenue: known(revenue),
      roas: over(spend, 'no_spend', () => round4(revenue / spend)),
      revenue_per_ad_spend: over(spend, 'no_spend', () => round4(sales / spend)),
      purchases: known(purchases),
      cpa: spendPer(purchases, 'no_attributed_purchases'),
      cost_per_new_customer: spendPer(newCustomers, 'no_attributed_new_customers'),
      ctr: over(impressions, 'no_impressions', () => round4(clicks / impressions)),
      cpc: over(clicks, 'no_clicks', () => Math.round(spend / clicks)),
      cpm: over(impressions, 'no_impressions', () => Math.round((spend * 1000) / impressions)),
    }
    const values = {} as Record<AdKpiKey, number | null>
    const reasons: Partial<Record<AdKpiKey, AdKpiNullReason>> = {}
    for (const [key, [value, reason]] of Object.entries(figures) as [AdKpiKey, Figure][]) {
      values[key] = value
      if (reason) reasons[key] = reason
    }
    return { values, reasons }
  }

  overview(query: AdOverviewQuery): AdvertisingOverview {
    const { from, to, days } = this.range(query.filters)
    const previousRange = { from: addDays(from, -days), to: addDays(from, -1) }
    const active = this.active()
    const keys = Object.keys(KPI_UNITS) as AdKpiKey[]
    const meta = {
      provider: query.provider,
      currency: this.currency(),
      timezone: 'Europe/London',
      range: { from, to },
      previousRange,
      previousUnavailable: null,
      environments: query.filters.environments.length > 0 ? [...query.filters.environments] : ['production' as const],
      nonProduction: query.filters.environments.some((environment) => environment !== 'production'),
      websiteId: query.filters.dimensions.website ?? null,
      model: query.model,
      lookbackDays: LOOKBACK_DAYS,
      attributionWindows: active ? [...ATTRIBUTION_WINDOWS] : [],
      asOf: this.freshness(),
    }
    const baseline = this.baseline()
    const dataQuality = demoDataQuality({ baseline, from, previousFrom: previousRange.from, ...demoSiteCoverage(from, to, baseline) })

    if (!active) {
      return {
        provider: query.provider,
        kpis: keys.map((key) => ({ key, ...KPI_UNITS[key], current: null, previous: null, deltaPct: null, nullReason: null })),
        notAvailable: Object.fromEntries(keys.map((key) => [key, 'needs_ad_platform' as const])),
        providerReported: { current: null, previous: null },
        campaigns: [],
        topCampaigns: [],
        limit: query.limit,
        reconciliation: null,
        dataQuality,
        meta,
      }
    }

    const current = this.period(days, 1, query.model)
    const previous = this.period(days, PREVIOUS_FACTOR, query.model)
    const kpis: AdKpi[] = keys.map((key) => ({
      key,
      ...KPI_UNITS[key],
      current: current.values[key],
      previous: previous.values[key],
      deltaPct: deltaPct(current.values[key], previous.values[key]),
      nullReason: current.reasons[key] ?? null,
    }))

    const counters = this.campaignCounters(days, 1, query.model)
    const rows: AdCampaignRow[] = CAMPAIGNS.map((campaign, index) => {
      const c = counters[index] as Counters
      return {
        id: campaign.id,
        name: campaign.name,
        status: campaign.status,
        effectiveStatus: campaign.effectiveStatus,
        spendMinor: c.spendMinor,
        impressions: c.impressions,
        clicks: c.clicks,
        ctr: ratio(c.clicks, c.impressions),
        cpcMinor: perUnit(c.spendMinor, c.clicks),
        tmt: tmtFigures(c),
        providerReported: providerFigures(c),
      }
    })
    const campaigns = [...rows].sort(
      (a, b) => b.spendMinor - a.spendMinor || (a.name ?? '').localeCompare(b.name ?? '') || a.id.localeCompare(b.id),
    )
    const topCampaigns = [...rows]
      .sort((a, b) => {
        if (a.tmt.roas === null || b.tmt.roas === null) return a.tmt.roas === null ? (b.tmt.roas === null ? 0 : 1) : -1
        return b.tmt.roas - a.tmt.roas || b.spendMinor - a.spendMinor || (a.name ?? '').localeCompare(b.name ?? '') || a.id.localeCompare(b.id)
      })
      .slice(0, query.limit)

    const totals = sumCounters(counters)
    const unmatched = this.unmatched(days, 1, query.model)
    const reconciliation: OverviewReconciliation = {
      attributedRevenueMinor: current.values.attributed_revenue ?? 0,
      purchases: current.values.purchases ?? 0,
      campaigns: { purchases: totals.tmtPurchases, revenueMinor: totals.tmtRevenueMinor },
      unmatchedPaid: { purchases: unmatched.purchases, revenueMinor: unmatched.revenueMinor },
      adCampaignsWithoutSpend: { purchases: 0, revenueMinor: 0 },
      campaignCreditOffSpendRows: { purchases: 0, revenueMinor: 0 },
    }

    return {
      provider: query.provider,
      kpis,
      notAvailable: {},
      providerReported: {
        current: providerFigures(totals),
        previous: providerFigures(sumCounters(this.campaignCounters(days, PREVIOUS_FACTOR, query.model))),
      },
      campaigns,
      topCampaigns,
      limit: query.limit,
      reconciliation,
      dataQuality,
      meta,
    }
  }

  trend(query: AdOverviewTrendQuery): AdvertisingOverviewTrend {
    const { from, to, days } = this.range(query.filters)
    const base = {
      provider: query.provider,
      granularity: query.granularity,
      meta: { currency: this.currency(), timezone: 'Europe/London', range: { from, to }, model: query.model, lookbackDays: LOOKBACK_DAYS },
    }
    if (!this.active()) return { ...base, points: [], totals: null, notAvailable: 'needs_ad_platform', salesUnavailable: null }

    const period = this.period(days, 1, query.model)
    const dates = Array.from({ length: days }, (_, index) => addDays(from, index))
    // Each series spread over the days by its own weekly wave, so the points sum to the KPI row exactly.
    const spend = splitInteger(period.values.ad_spend ?? 0, dates.map((day) => weight(day, 0)))
    const revenue = splitInteger(period.values.attributed_revenue ?? 0, dates.map((day) => weight(day, 2)))
    const purchases = splitInteger(period.values.purchases ?? 0, dates.map((day) => weight(day, 2)))
    const sales = allSales(days, 1)
    const orders = splitInteger(sales.orders, dates.map((day) => weight(day, 1)))
    const gross = splitInteger(sales.revenueMinor, dates.map((day) => weight(day, 1)))
    const daily: AdTrendPoint[] = dates.map((day, index) => ({
      date: day,
      endDate: day,
      spendMinor: spend[index] ?? 0,
      attributedRevenueMinor: revenue[index] ?? 0,
      purchases: purchases[index] ?? 0,
      orders: orders[index] ?? 0,
      revenueGrossMinor: gross[index] ?? 0,
    }))
    const points = query.granularity === 'day' ? daily : weekly(daily)
    return {
      ...base,
      points,
      totals: {
        spendMinor: period.values.ad_spend ?? 0,
        attributedRevenueMinor: period.values.attributed_revenue ?? 0,
        purchases: period.values.purchases ?? 0,
        orders: sales.orders,
        revenueGrossMinor: sales.revenueMinor,
      },
      notAvailable: null,
      salesUnavailable: null,
    }
  }

  reconciliation(query: AdReconciliationQuery): AdAttributionReconciliation {
    const days = dayCount(query.from, query.to)
    const active = this.active()
    const rows = active ? objectRows(query.level, days, query.model) : []
    // Listed at campaign level only: the demo keeps no ad sets or ads for the unmapped account.
    const notImportedPurchases =
      active && query.level === 'campaign' ? Math.round(NOT_IMPORTED.purchasesPerDay * days * MODEL_FACTOR[query.model]) : 0
    if (notImportedPurchases > 0) {
      rows.push({
        id: NOT_IMPORTED.id,
        name: NOT_IMPORTED.name,
        spendMinor: 0,
        deliveryInRange: false,
        ...figures(null, 'not_imported', {
          purchases: notImportedPurchases,
          revenueMinor: notImportedPurchases * NOT_IMPORTED.revenuePerPurchaseMinor,
        }, query.model),
      })
    }
    rows.sort(
      (a, b) =>
        b.spendMinor - a.spendMinor ||
        Number(b.deliveryInRange) - Number(a.deliveryInRange) ||
        b.tmt.purchases - a.tmt.purchases ||
        a.id.localeCompare(b.id),
    )
    const unmatched = active ? this.unmatched(days, 1, query.model) : { purchases: 0, revenueMinor: 0 }
    const lines = [
      { kind: 'tmt_not_at_level' as const, ...figures(null, 'not_a_meta_object', { purchases: 0, revenueMinor: 0 }, query.model) },
      { kind: 'tmt_unmatched_paid' as const, ...figures(null, 'not_a_meta_object', unmatched, query.model) },
    ]
    const metaRows = rows.filter((row) => row.meta.purchases !== null)
    const metaTotal = {
      purchases: metaRows.reduce((acc, row) => acc + (row.meta.purchases ?? 0), 0),
      revenueMinor: metaRows.reduce((acc, row) => acc + (row.meta.revenueMinor ?? 0), 0),
    }
    const sumTmt = (list: readonly ReconciliationFigures[]) => ({
      purchases: list.reduce((acc, item) => acc + item.tmt.purchases, 0),
      revenueMinor: list.reduce((acc, item) => acc + item.tmt.revenueMinor, 0),
    })
    return {
      start: query.from,
      end: query.to,
      level: query.level,
      currency: this.currency(),
      model: query.model,
      attributionWindows: active ? [...ATTRIBUTION_WINDOWS] : [],
      tmtLookbackDays: LOOKBACK_DAYS,
      rows,
      lines,
      objectsTotal: figures(metaTotal, null, sumTmt(rows), query.model),
      total: withMetaOnly(figures(metaTotal, null, sumTmt([...rows, ...lines]), query.model)),
      causes: [...DEMO_CAUSES],
      asOf: this.freshness(),
    }
  }

  /** The README's CSV: one line per object, the two lines, `objects_total`, then `total`. */
  reconciliationCsv(query: AdReconciliationQuery): Blob {
    const report = this.reconciliation(query)
    const windows = report.attributionWindows.map((window) => window.split(',').join(' + ')).join(' | ')
    const meta = windows ? `Meta-reported (${windows})` : 'Meta-reported'
    const tmt = `TmT-attributed (${report.model})`
    const header = [
      'row_type',
      'id',
      'name',
      'delivery_in_range',
      'Meta-reported spend (minor units)',
      'currency',
      `${meta} purchases`,
      `${tmt} purchases`,
      'Purchases difference (TmT-attributed - Meta-reported)',
      'Purchases difference % of Meta-reported',
      `${meta} revenue (minor units)`,
      `${tmt} revenue (minor units)`,
      '"Revenue difference (TmT-attributed - Meta-reported, minor units)"',
      'Revenue difference % of Meta-reported',
      'Meta-reported not available',
      'Purchases difference % not available',
      'Revenue difference % not available',
    ]
    const cell = (value: number | string | null) => (value === null ? '' : String(value))
    const line = (rowType: string, item: ReconciliationFigures, object?: ReconciliationObjectRow) =>
      [
        rowType,
        object ? object.id : '',
        object ? (object.name ?? '') : '',
        object ? String(object.deliveryInRange) : '',
        object ? String(object.spendMinor) : '',
        report.currency,
        cell(item.meta.purchases),
        cell(item.tmt.purchases),
        cell(item.diff.purchases),
        cell(item.diff.purchasesPct),
        cell(item.meta.revenueMinor),
        cell(item.tmt.revenueMinor),
        cell(item.diff.revenueMinor),
        cell(item.diff.revenuePct),
        cell(item.meta.notAvailable),
        cell(item.diff.purchasesPctNullReason),
        cell(item.diff.revenuePctNullReason),
      ].join(',')
    const lines = [
      header.join(','),
      ...report.rows.map((row) => line('object', row, row)),
      ...report.lines.map((item) => line(item.kind, item)),
      line('objects_total', report.objectsTotal),
      line('total', report.total),
    ]
    return new Blob([lines.join('\n')], { type: 'text/csv' })
  }
}

/** Seven-day buckets counted from the first day; the last may be shorter. */
function weekly(daily: readonly AdTrendPoint[]): AdTrendPoint[] {
  const buckets: AdTrendPoint[] = []
  for (let start = 0; start < daily.length; start += 7) {
    const days = daily.slice(start, start + 7)
    const first = days[0] as AdTrendPoint
    const last = days[days.length - 1] as AdTrendPoint
    buckets.push({
      date: first.date,
      endDate: last.date,
      spendMinor: days.reduce((acc, day) => acc + day.spendMinor, 0),
      attributedRevenueMinor: days.reduce((acc, day) => acc + day.attributedRevenueMinor, 0),
      purchases: days.reduce((acc, day) => acc + day.purchases, 0),
      orders: days.reduce((acc, day) => acc + (day.orders ?? 0), 0),
      revenueGrossMinor: days.reduce((acc, day) => acc + (day.revenueGrossMinor ?? 0), 0),
    })
  }
  return buckets
}

/** A period's every completed order and its gross, at `factor` of the demo's daily rate. */
function allSales(days: number, factor: number): { orders: number; revenueMinor: number } {
  const orders = Math.round(ALL_SALES.ordersPerDay * days * factor)
  return { orders, revenueMinor: orders * ALL_SALES.averageOrderMinor }
}

/** The gap by the README's rule: TmT − Meta, and that as a percent of Meta's figure. */
function diffOf(meta: number | null, tmt: number): { value: number | null; pct: number | null; reason: ReconciliationDiff['purchasesNullReason']; pctReason: ReconciliationDiff['purchasesNullReason'] } {
  if (meta === null) return { value: null, pct: null, reason: 'meta_not_available', pctReason: 'meta_not_available' }
  if (meta === 0) return { value: tmt, pct: null, reason: null, pctReason: 'meta_zero' }
  return { value: tmt - meta, pct: round2(((tmt - meta) / meta) * 100), reason: null, pctReason: null }
}

function figures(
  meta: { purchases: number; revenueMinor: number } | null,
  notAvailable: MetaReported['notAvailable'],
  tmt: { purchases: number; revenueMinor: number },
  model: AttributionModel,
): ReconciliationFigures {
  const purchases = diffOf(meta?.purchases ?? null, tmt.purchases)
  const revenue = diffOf(meta?.revenueMinor ?? null, tmt.revenueMinor)
  const tmtSide: TmtAttributed = { tmtAttributed: true, model, purchases: tmt.purchases, revenueMinor: tmt.revenueMinor }
  return {
    meta: {
      providerAttributed: true,
      purchases: meta?.purchases ?? null,
      revenueMinor: meta?.revenueMinor ?? null,
      notAvailable: meta ? null : notAvailable,
    },
    tmt: tmtSide,
    diff: {
      purchases: purchases.value,
      revenueMinor: revenue.value,
      purchasesPct: purchases.pct,
      revenuePct: revenue.pct,
      purchasesNullReason: purchases.reason,
      revenueNullReason: revenue.reason,
      purchasesPctNullReason: purchases.pctReason,
      revenuePctNullReason: revenue.pctReason,
    },
  }
}

/** The README's `meta_only`: Meta − TmT when Meta reported more; otherwise null, with why. */
function metaOnlyOf(meta: number | null, tmt: number): { value: number | null; reason: ReconciliationMetaOnly['purchasesNullReason'] } {
  if (meta === null) return { value: null, reason: 'meta_not_available' }
  if (tmt >= meta) return { value: null, reason: 'tmt_not_below_meta' }
  return { value: meta - tmt, reason: null }
}

function withMetaOnly(total: ReconciliationFigures): ReconciliationTotal {
  const purchases = metaOnlyOf(total.meta.purchases, total.tmt.purchases)
  const revenue = metaOnlyOf(total.meta.revenueMinor, total.tmt.revenueMinor)
  return {
    ...total,
    metaOnly: {
      providerAttributed: true,
      purchases: purchases.value,
      revenueMinor: revenue.value,
      purchasesNullReason: purchases.reason,
      revenueNullReason: revenue.reason,
    },
  }
}

/** Every object at `level` with its counters: a campaign's split over its ad sets, an ad set's over its ads. */
function objectRows(level: AdvertisingLevel, days: number, model: AttributionModel): ReconciliationObjectRow[] {
  const rows: ReconciliationObjectRow[] = []
  for (const campaign of CAMPAIGNS) {
    const counters = countersFor(campaign.rates, days, 1, model)
    const objects: { id: string; name: string; counters: Counters }[] = []
    if (level === 'campaign') objects.push({ id: campaign.id, name: campaign.name, counters })
    else {
      const adSets = splitCounters(counters, campaign.adSets.map((adSet) => adSet.share))
      campaign.adSets.forEach((adSet, index) => {
        const adSetCounters = adSets[index] as Counters
        if (level === 'ad_set') objects.push({ id: adSet.id, name: adSet.name, counters: adSetCounters })
        else {
          const ads = adSet.children ?? []
          const split = splitCounters(adSetCounters, ads.map((ad) => ad.share))
          ads.forEach((ad, adIndex) => objects.push({ id: ad.id, name: ad.name, counters: split[adIndex] as Counters }))
        }
      })
    }
    for (const object of objects) {
      const c = object.counters
      rows.push({
        id: object.id,
        name: object.name,
        spendMinor: c.spendMinor,
        deliveryInRange: true,
        ...figures(
          { purchases: c.metaPurchases, revenueMinor: c.metaRevenueMinor },
          null,
          { purchases: c.tmtPurchases, revenueMinor: c.tmtRevenueMinor },
          model,
        ),
      })
    }
  }
  return rows
}

function splitCounters(counters: Counters, shares: readonly number[]): Counters[] {
  const keys = Object.keys(counters) as (keyof Counters)[]
  const parts = keys.map((key) => splitInteger(counters[key], shares))
  return shares.map((_, index) => {
    const part = {} as Counters
    keys.forEach((key, keyIndex) => {
      part[key] = parts[keyIndex]?.[index] ?? 0
    })
    return part
  })
}

/** One store per demo client and tenant. */
const stores = new WeakMap<object, Map<string, MockAdvertisingOverviewStore>>()

export function advertisingOverviewStoreFor(
  owner: object,
  tenantId: string,
  connection: () => MetaIntegrationState,
  currency: () => string,
  now: () => Date,
  baseline: () => string | null = () => null,
): MockAdvertisingOverviewStore {
  let byTenant = stores.get(owner)
  if (!byTenant) {
    byTenant = new Map()
    stores.set(owner, byTenant)
  }
  let store = byTenant.get(tenantId)
  if (!store) {
    store = new MockAdvertisingOverviewStore(connection, currency, now, baseline)
    byTenant.set(tenantId, store)
  }
  return store
}
